import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { wsUrl, getToken, getPosts, createPost, getWeeklyCalls, reserveCall, getAnuncios, adminCreateAnuncio, adminDeleteAnuncio } from "@/lib/api";
import { Megaphone, Trash2 } from "lucide-react";

function ChatEnVivo() {
  const [msgs, setMsgs] = useState([]);
  const [online, setOnline] = useState(0);
  const [draft, setDraft] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const url = wsUrl(`/api/ws/chat?token=${getToken()}&canal=general`);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "history") { setMsgs(data.messages); setOnline(data.online); }
      else if (data.type === "message") { setMsgs((m) => [...m, data.message]); }
      else if (data.type === "presence") { setOnline(data.online); }
    };
    return () => ws.close();
  }, []);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);

  const enviar = () => {
    const texto = draft.trim();
    if (!texto || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ texto }));
    setDraft("");
  };

  const myId = (() => {
    try { return JSON.parse(atob(getToken().split(".")[1])).sub; } catch { return null; }
  })();

  return (
    <div className="chat-demo" style={{ maxWidth: 780, height: 540 }} data-testid="campus-chat">
      <div className="chat-head">
        <span className="live" /><b>#general — Comunidad Liberti</b>
        <span data-testid="online-count">{connected ? `${online} en línea` : "conectando…"}</span>
      </div>
      <div className="chat-body" ref={bodyRef}>
        {msgs.map((m, i) => {
          const me = m.user_id === myId && !m.bot;
          return (
            <div className={`msg ${m.bot ? "bot" : ""} ${me ? "me" : ""}`} key={i}>
              <div className="av">{m.avatar}</div>
              <div className="bub"><b>{m.nombre}</b>{m.texto}</div>
            </div>
          );
        })}
      </div>
      <div className="chat-input">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && enviar()}
          placeholder="Escribe un mensaje… (pregunta a LIBI lo que quieras)" data-testid="chat-input" />
        <button className="btn btn-gold btn-sm" onClick={enviar} data-testid="chat-send">Enviar</button>
      </div>
    </div>
  );
}

function Llamadas() {
  const [calls, setCalls] = useState([]);
  const load = () => getWeeklyCalls().then(setCalls).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    try {
      const r = await reserveCall(id);
      if (r.reservada) toast.success("Plaza reservada · te enviamos el enlace de Zoom por email.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reservar.");
    }
  };

  return (
    <div style={{ maxWidth: 780 }}>
      <p style={{ color: "var(--muted)", fontSize: 13.5, fontWeight: 300, lineHeight: 1.7, marginBottom: 22 }}>
        Todas las llamadas son en directo por Zoom y quedan grabadas en el campus. Horarios en hora de Madrid y Ciudad de México.
      </p>
      <div className="calls" data-testid="calls-list">
        {calls.map((c) => (
          <div className="call" key={c.id}>
            <div className="date"><b>{c.dia}</b><span>{c.mes}</span></div>
            <div className="info"><h4>{c.titulo}</h4><div className="ci">{c.hora} · <b>{c.plazas}</b></div></div>
            <button className={`btn btn-sm ${c.reservada ? "btn-ghost" : "btn-gold"}`} data-testid={`reservar-${c.id}`} onClick={() => toggle(c.id)}>
              {c.reservada ? "✓ Plaza reservada" : "Reservar plaza"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Muro({ nombre }) {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState("");
  const load = () => getPosts().then(setPosts).catch(() => {});
  useEffect(() => { load(); }, []);

  const publicar = async () => {
    if (draft.trim().length < 2) return;
    try { await createPost(draft.trim()); setDraft(""); load(); }
    catch (e) { toast.error("No se pudo publicar."); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="feed" data-testid="muro">
      <div className="post">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Comparte un logro, una duda o un recurso con la comunidad…"
          data-testid="muro-input"
          style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "12px 14px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 14, minHeight: 70, resize: "vertical" }} />
        <button className="btn btn-gold btn-sm" style={{ marginTop: 12 }} data-testid="muro-publicar" onClick={publicar}>Publicar →</button>
      </div>
      {posts.map((p) => (
        <div className="post" key={p.id}>
          <div className="ph"><div className="av">{p.avatar}</div><div><div className="pn">{p.nombre}</div><div className="pt">{fmt(p.timestamp)}</div></div></div>
          <p>{p.texto}</p>
        </div>
      ))}
    </div>
  );
}

function AvisosCanal({ esAdmin }) {
  const [anuncios, setAnuncios] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const load = () => getAnuncios().then(setAnuncios).catch(() => {});
  useEffect(() => { load(); }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  const publicar = async () => {
    if (!titulo.trim()) { toast.error("Escribe un título"); return; }
    try { await adminCreateAnuncio({ titulo, cuerpo }); setTitulo(""); setCuerpo(""); load(); toast.success("Anuncio publicado"); }
    catch (e) { toast.error("No se pudo publicar."); }
  };
  const borrar = async (id) => { if (!window.confirm("¿Eliminar anuncio?")) return; await adminDeleteAnuncio(id); load(); };

  return (
    <div className="feed" data-testid="avisos-canal">
      {esAdmin && (
        <div className="post" data-testid="anuncio-composer">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "var(--gold)" }}><Megaphone size={16} /><b style={{ fontSize: 13 }}>Publicar anuncio oficial</b></div>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título del anuncio" data-testid="anuncio-titulo"
            style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "10px 12px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 14, marginBottom: 8 }} />
          <textarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} placeholder="Mensaje para toda la comunidad…" data-testid="anuncio-cuerpo"
            style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "12px 14px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 14, minHeight: 70, resize: "vertical" }} />
          <button className="btn btn-gold btn-sm" style={{ marginTop: 10 }} data-testid="anuncio-publicar" onClick={publicar}>Publicar anuncio →</button>
        </div>
      )}
      {anuncios.length === 0 ? (
        <p style={{ color: "var(--muted)" }} data-testid="avisos-vacio">Todavía no hay anuncios del equipo.</p>
      ) : anuncios.map((a) => (
        <div className="post anuncio-post" key={a.id} data-testid={`anuncio-${a.id}`}>
          <div className="ph">
            <div className="av" style={{ background: "var(--gold)", color: "#fff" }}><Megaphone size={15} /></div>
            <div style={{ flex: 1 }}><div className="pn">{a.titulo}</div><div className="pt">{a.autor} · {fmt(a.created_at)}</div></div>
            {esAdmin && <button className="abtn danger" onClick={() => borrar(a.id)} style={{ padding: "4px 8px" }}><Trash2 size={13} /></button>}
          </div>
          {a.cuerpo && <p style={{ whiteSpace: "pre-wrap" }}>{a.cuerpo}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Comunidad({ nombre, esAdmin }) {
  const [sub, setSub] = useState("chat");
  return (
    <>
      <div className="main-head">
        <div>
          <h1>Comunidad</h1>
          <p className="sub">Chat en vivo con LIBI, llamadas semanales, muro y anuncios oficiales del equipo.</p>
        </div>
      </div>
      <div className="comu-tabs">
        {[["chat", "◉ Chat en vivo"], ["llamadas", "◈ Llamadas semanales"], ["feed", "◇ Muro"], ["avisos", "◆ Avisos del equipo"]].map(([id, lbl]) => (
          <button key={id} data-testid={`comu-tab-${id}`} className={`comu-tab ${sub === id ? "on" : ""}`} onClick={() => setSub(id)}>{lbl}</button>
        ))}
      </div>
      {sub === "chat" && <ChatEnVivo />}
      {sub === "llamadas" && <Llamadas />}
      {sub === "feed" && <Muro nombre={nombre} />}
      {sub === "avisos" && <AvisosCanal esAdmin={esAdmin} />}
    </>
  );
}
